import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { Box, Grid, Typography, Stack, useTheme } from "@mui/material";
import { useResponsive } from "../../hooks/useResponsive";
import { useTranslation } from "react-i18next";
import colors from "../../designTokens/colors.esm";
import HighlightedText from "../../components/typography/HighlightedText";

 

// 🎯 최적화된 비디오 소스들 (평균 61% 용량 감소)
const usp1Video =
  "https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/videos%2Flanding%2Fusp1_optimized.mp4?alt=media";
const usp2Video =
  "https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/videos%2Flanding%2Fusp2_optimized.mp4?alt=media";
const usp3Video =
  "https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/videos%2Flanding%2Fusp3_optimized.mp4?alt=media";
const usp4Video =
  "https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/videos%2Flanding%2Fusp4_optimized.mp4?alt=media";

// USP 데이터 키 정의
const USP_KEYS = [
  {
    id: "explore",
    titleKey: "usp.features.explore.title",
    videoSrc: usp1Video,
  },
  {
    id: "analysis",
    titleKey: "usp.features.analysis.title",
    videoSrc: usp2Video,
  },
  {
    id: "discovery",
    titleKey: "usp.features.discovery.title",
    videoSrc: usp3Video,
  },
  {
    id: "routine",
    titleKey: "usp.features.routine.title",
    videoSrc: usp4Video,
  },
];

// 🎯 UspItem 컴포넌트 (React.memo로 최적화)
const UspItem = React.memo(
  ({ usp, index, isMobileOnly, onVideoLoad, isVideoLoaded, t }) => {
    const theme = useTheme();
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const [isInView, setIsInView] = useState(false);
    const [hasPlayed, setHasPlayed] = useState(false);
    const [videoState, setVideoState] = useState("loading"); // loading, loaded, error, playing
    const [videoAspectRatio, setVideoAspectRatio] = useState("16/9"); // 🎯 추가: 비디오 비율 상태
    const [isHovered, setIsHovered] = useState(false); // 🎯 추가: hover 상태

    // 🎯 Intersection Observer로 화면 진입 감지 (최적화)
    useEffect(() => {
      if (!containerRef.current) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsInView(true);
          }
        },
        {
          threshold: 0.1, // 10%만 보여도 로드 시작 (더 빠른 반응)
          rootMargin: "300px", // 300px 전에 미리 로드 시작
        }
      );

      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }, []);

    // 🎯 페이지 로드 시 즉시 비디오 로드 (더 빠른 경험)
    useEffect(() => {
      const startPlayback = async () => {
        try {
          if (videoRef.current) {
            // 메타데이터만 먼저 로드
            await videoRef.current.load();
            setVideoState("loaded");
            onVideoLoad(usp.id);
          }
        } catch (error) {
          // Video load failed silently
          setVideoState("error");
        }
      };

      // 약간의 지연 후 로드 시작 (페이지 로딩 최적화)
      const timer = setTimeout(startPlayback, 100);
      return () => clearTimeout(timer);
    }, [usp.id, onVideoLoad]);

    // 🎯 hover 시에만 비디오 재생/정지
    useEffect(() => {
      if (
        !videoRef.current ||
        videoState === "loading" ||
        videoState === "error"
      )
        return;

      const playVideo = async () => {
        try {
          if (isHovered) {
            await videoRef.current.play();
            setVideoState("playing");
            setHasPlayed(true);
          } else {
            videoRef.current.pause();
            videoRef.current.currentTime = 0; // 처음으로 되감기
            setVideoState("loaded");
          }
        } catch (error) {
          // Video hover play/pause failed silently
        }
      };

      playVideo();
    }, [isHovered, videoState, usp.id]);

    // 🎯 마우스 이벤트 핸들러
    const handleMouseEnter = useCallback(() => {
      setIsHovered(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
      setIsHovered(false);
    }, []);

    // 비디오 이벤트 핸들러들
    const handleLoadedData = useCallback(() => {
      onVideoLoad(usp.id);
      setVideoState("loaded");
    }, [onVideoLoad, usp.id]);

    const handleLoadStart = useCallback(() => {
      setVideoState("loading");
    }, []);

    const handleError = useCallback(
      () => {
        // Video error occurred silently
        setVideoState("error");
      },
      [usp.id]
    );

    // 🎯 수정: 비디오 메타데이터에서 실제 비율 계산
    const handleLoadedMetadata = useCallback(() => {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;

        // 실제 비디오 비율 계산
        const { videoWidth, videoHeight } = videoRef.current;
        if (videoWidth && videoHeight) {
          const calculatedRatio = videoWidth / videoHeight;
          setVideoAspectRatio(calculatedRatio.toFixed(2)); // 소수점 2자리까지
          // Video dimensions calculated
        }
      }
    }, [usp.id]);

    return (
      <Box
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: "relative",
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: colors.inkGray[1], // 사이트 배경색과 동일
          border: `1px solid ${theme.palette.primary.main}`, // 🎯 primary main 색상 border 추가
          cursor: "pointer",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            // 🎯 border 효과 제거
          },
        }}
      >
        {/* 🎯 타이틀 섹션 - 맨 위로 이동 */}
        <Box
          sx={{
            p: isMobileOnly ? 2 : 3,
            // backgroundColor: theme.palette.primary.main, // 🎯 primary main으로 변경
            color: theme.palette.text.primary,
          }}
        >
          <Typography
            variant={isMobileOnly ? "h6" : "h5"}
            fontWeight={700}
            sx={{
              fontFamily: "Neuton, serif",
              letterSpacing: "-0.01em",
            }}
          >
            {index + 1}. {t(usp.titleKey)}
          </Typography>
        </Box>

        {/* 🎯 비디오 영역 - 패딩 16px 추가 */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: videoAspectRatio, // 🎯 실제 비디오 비율 사용
            backgroundColor: colors.inkGray[1], // 컨테이너 배경은 사이트 배경색과 동일
            overflow: "hidden",
            padding: {
              xs: "20px",
              md: "32px",
            },
          }}
        >
          {/* 🎯 개선된 비디오 엘리먼트 - 로딩 최적화 */}
          <video
            ref={videoRef}
            key={usp.id}
            muted
            loop
            playsInline
            preload="metadata" // 메타데이터를 미리 로드하여 빠른 시작
            loading="eager" // 즉시 로딩
            onLoadStart={handleLoadStart}
            onLoadedData={handleLoadedData}
            onLoadedMetadata={handleLoadedMetadata}
            onError={handleError}
            // 🎯 브라우저 정책 준수를 위한 추가 속성들
            crossOrigin="anonymous"
            controlsList="nodownload"
            disablePictureInPicture
            poster="" // 포스터 없음으로 빠른 로딩
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain", // 🎯 cover에서 contain으로 변경 - 원본 비율 유지
              transform: "scale(1.02)", // 🎯 2% 확대로 인코딩 border 제거
              display: videoState === "error" ? "none" : "block",
              borderRadius: "12px", // 비디오 자체에도 radius 추가
            }}
          >
            <source src={usp.videoSrc} type="video/mp4" />
            영상을 지원하지 않는 브라우저입니다.
          </video>

          {/* 로딩/에러 상태 표시 */}
          {(videoState === "loading" || videoState === "error") && (
            <Box
              sx={{
                position: "absolute",
                top: "24px",
                left: "24px",
                width: "calc(100% - 48px)",
                height: "calc(100% - 48px)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: theme.palette.grey[200],
                gap: 1,
                borderRadius: "12px",
              }}
                          >
                <Typography variant="body2" color="text.secondary">
                  {videoState === "loading"
                    ? t("usp.video.loading")
                    : t("usp.video.hoverToPlay")}
                </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);

UspItem.displayName = "UspItem";

const UspTemplate = ({}) => {
  const theme = useTheme();
  const { isMobileOnly } = useResponsive();
  const { t, i18n } = useTranslation();
  const [loadedVideos, setLoadedVideos] = useState({});

  const handleVideoLoad = useCallback((uspId) => {
    setLoadedVideos((prev) => ({
      ...prev,
      [uspId]: true,
    }));
  }, []);

  const responsiveConfig = useMemo(
    () => ({
      logoVariant: isMobileOnly ? "h3" : "h2",
      titleVariant: isMobileOnly ? "h5" : "h4",
      spacing: isMobileOnly ? 6 : 12,
      py: isMobileOnly ? 0 : 0,
      px: isMobileOnly ? 2 : 0,
    }),
    [isMobileOnly]
  );

  // 세번째 메시지 렌더링 (ScrubbingVideoWithFullPage와 동일한 로직)
  const renderThirdMessage = () => {
    if (i18n.language === "ko") {
      return (
        <HighlightedText>
          나만의 <HighlightedText.Bold>주제와</HighlightedText.Bold> 쓰기{" "}
          <HighlightedText.Bold>루틴</HighlightedText.Bold>으로
          <HighlightedText.Break />
          <HighlightedText.Bold>'나'</HighlightedText.Bold>라는{" "}
          <HighlightedText.Bold>브랜드</HighlightedText.Bold>를 채워나가세요.
        </HighlightedText>
      );
    }

    // 영어 버전
    return (
      <HighlightedText>
        Build <HighlightedText.Bold>your own</HighlightedText.Bold>{" "}
        <HighlightedText.Bold>brand</HighlightedText.Bold> with
        <HighlightedText.Break />
        <HighlightedText.Bold>unique topics</HighlightedText.Bold> and{" "}
        <HighlightedText.Bold>routines</HighlightedText.Bold>.
      </HighlightedText>
    );
  };

  return (
    <Stack
      spacing={responsiveConfig.spacing}
      alignItems="center"
      sx={{
        width: "100%",
        py: responsiveConfig.py,
        px: responsiveConfig.px,
      }}
    >
      <Stack spacing={2} alignItems="center">
        {/* 텍스트 로고 */}
        <Typography
          component="h1"
          variant={responsiveConfig.logoVariant}
          fontWeight={700}
          textAlign="center"
          sx={{
            fontFamily: "Neuton, serif",
            letterSpacing: "-0.01em",
            color: theme.palette.text.primary,
          }}
        >
          My Threads Insight
        </Typography>

        {/* 상단에 3번째 메시지 추가 - 반응형 대응 */}
        <Typography
          variant={responsiveConfig.titleVariant}
          fontWeight={300}
          textAlign="center"
          sx={{
            fontFamily: "Neuton, serif",
            letterSpacing: "-0.01em",
            color: theme.palette.text.primary,
            whiteSpace: "pre-line",
            wordBreak: "normal",
            lineHeight: isMobileOnly ? 1.25 : 1.4,
            fontSize: isMobileOnly ? "1.25rem" : undefined, // 모바일에서 폰트 크기 조정
            maxWidth: isMobileOnly ? "95%" : "90%", // 반응형 최대 너비
          }}
        >
          {renderThirdMessage()}
        </Typography>
      </Stack>
      <Grid
        container
        spacing={{
          xs: 4,
          md: 8,
        }}
        sx={{
          maxWidth: isMobileOnly ? "100%" : "1200px",
          width: "100%",
        }}
              >
          {USP_KEYS.map((usp, index) => (
            <Grid
              key={usp.id}
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <UspItem
                usp={usp}
                index={index}
                isMobileOnly={isMobileOnly}
                onVideoLoad={handleVideoLoad}
                isVideoLoaded={loadedVideos[usp.id] || false}
                t={t}
              />
            </Grid>
          ))}
      </Grid>
    </Stack>
  );
};

export default React.memo(UspTemplate);
